import React, { useState, useEffect } from 'react'
import { NavLink, useNavigate, useParams } from 'react-router-dom'
import { Trash2, Folder, ChevronDown, ChevronRight, FileText } from 'lucide-react'
import { collection, onSnapshot } from 'firebase/firestore'
import { db } from '../../../lib/firebase'
import type { CaseSummary, StepStatus } from '../../../hooks/useCases'
import styles from './CaseListItem.module.css'

interface DocumentSummary {
  id: string
  document_type: string | null
  tax_year: string | null
  filename: string
}

interface CaseListItemProps {
  caseData: CaseSummary
  onDelete?: (caseId: string) => void
}

/**
 * Get CSS class for step indicator based on status
 */
const getStepClass = (status: StepStatus): string => {
  switch (status) {
    case 'complete': return styles.stepComplete
    case 'active': return styles.stepActive
    case 'error': return styles.stepError
    default: return styles.stepPending
  }
}

const getRelativeTime = (date: Date) => {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffDays > 0) return `${diffDays}d ago`
  if (diffHours > 0) return `${diffHours}h ago`
  if (diffMins > 0) return `${diffMins}m ago`
  return 'just now'
}

const CaseListItem: React.FC<CaseListItemProps> = ({ caseData, onDelete }) => {
  const navigate = useNavigate()
  const [isExpanded, setIsExpanded] = useState(false)
  const [documents, setDocuments] = useState<DocumentSummary[]>([])
  const [isLoadingDocs, setIsLoadingDocs] = useState(false)
  const { caseId } = useParams<{ caseId: string }>()


  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onDelete?.(caseData.id)
  }

  const handleToggleExpand = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsExpanded(!isExpanded)
  }

  const handleDocClick = (e: React.MouseEvent, docId: string) => {
    e.preventDefault()
    e.stopPropagation()
    navigate(`/quick-check/${caseData.id}/documents/${docId}`)
  }

  // Fetch documents when expanded
  useEffect(() => {
    if (!isExpanded || documents.length > 0) return

    setIsLoadingDocs(true)
    const docsRef = collection(db, 'cases', caseData.id, 'documents')
    
    const unsubscribe = onSnapshot(
      docsRef,
      (snapshot) => {
        const docs: DocumentSummary[] = snapshot.docs.map((doc) => {
          const data = doc.data()
          return {
            id: doc.id,
            document_type: data.document_type || null,
            tax_year: data.tax_year || null,
            filename: data.filename || doc.id,
          }
        })
        // Sort by document type (classified first), then by filename
        docs.sort((a, b) => {
          if (a.document_type && !b.document_type) return -1
          if (!a.document_type && b.document_type) return 1
          return (a.filename || '').localeCompare(b.filename || '')
        })
        setDocuments(docs)
        setIsLoadingDocs(false)
      },
      (error) => {
        console.error('Error fetching documents:', error)
        setIsLoadingDocs(false)
      }
    )

    return () => unsubscribe()
  }, [isExpanded, caseData.id, documents.length])

  const { steps, completedSteps, totalSteps, documentsCount } = caseData

  // Check if this is the first step (documents pending = call to action)
  const isFirstStep = steps.documents === 'pending' || (steps.documents === 'active' && completedSteps === 0)

  const hasDocuments = documentsCount > 0

  // Format document type for display
  const formatDocType = (docType: string | null): string => {
    if (!docType) return 'Unclassified'
    // Convert IRS1040 -> 1040, IRSW2 -> W-2, etc.
    return docType
      .replace(/^IRS/, '')
      .replace(/^US/, '')
      .replace(/(\d+)([A-Z])/, '$1-$2')
  }

  return (
    <div className={styles.caseItemWrapper}>
      <NavLink
        to={`/quick-check/${caseData.id}/documents`}
        className={({ isActive }) =>
          `${styles.caseItem} ${isActive || caseId === caseData.id ? styles.selected : ''}`
        }
      >
        {/* Header: Name + ID + Delete */}
        <div className={styles.caseHeader}>
          <div className={styles.caseNameRow}>
            <span className={styles.caseName}>{caseData.borrowerName}</span>
            <span className={styles.caseId}>{caseData.shortId}</span>
          </div>
          {onDelete && (
            <button
              className={styles.deleteButton}
              onClick={handleDelete}
              title="Delete case"
              aria-label="Delete case"
              type="button"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>

        {/* Progress: 5 dots showing workflow status */}
        <div className={styles.progressRow}>
          <div className={styles.stepDots}>
            <div className={`${styles.stepDot} ${getStepClass(steps.documents)}`} title="Documents" />
            <div className={`${styles.stepDot} ${getStepClass(steps.income)}`} title="Income" />
            <div className={`${styles.stepDot} ${getStepClass(steps.cashflow)}`} title="Cash Flow" />
            <div className={`${styles.stepDot} ${getStepClass(steps.assets)}`} title="Assets" />
            <div className={`${styles.stepDot} ${getStepClass(steps.debt)}`} title="Debt" />
          </div>
          <span className={styles.progressCount}>{completedSteps}/{totalSteps}</span>
        </div>

        {/* Footer: Current step + Time */}
        <div className={styles.caseFooter}>
          <span className={`${styles.currentStep} ${isFirstStep ? styles.ctaStep : ''}`}>
            {caseData.currentStep}
          </span>
          <span className={styles.timeInfo}>{getRelativeTime(caseData.updatedAt)}</span>
        </div>

        {/* Documents section with expand/collapse */}
        {hasDocuments && (
          <div className={styles.docsSection}>
            <button
              type="button"
              className={styles.docsFolderIndicator}
              onClick={handleToggleExpand}
            >
              {isExpanded ? (
                <ChevronDown size={14} className={styles.folderIcon} />
              ) : (
                <ChevronRight size={14} className={styles.folderIcon} />
              )}
              <Folder size={14} className={styles.folderIcon} />
              <span className={styles.docsFolderLabel}>Documents</span>
              <span className={styles.docsCount}>{documentsCount}</span>
            </button>

            {/* Expanded document list */}
            {isExpanded && (
              <div className={styles.docsList}>
                {isLoadingDocs ? (
                  <div className={styles.docItem}>
                    <span className={styles.docMeta}>Loading...</span>
                  </div>
                ) : (
                  documents.map((doc) => (
                    <button
                      key={doc.id}
                      type="button"
                      className={styles.docItem}
                      onClick={(e) => handleDocClick(e, doc.id)}
                    >
                      <FileText size={12} className={styles.docIcon} />
                      <div className={styles.docInfo}>
                        <span className={styles.docName}>
                          {formatDocType(doc.document_type)}
                        </span>
                        <span className={styles.docMeta}>
                          {doc.tax_year ? `Tax Year ${doc.tax_year}` : doc.filename}
                        </span>
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </NavLink>
    </div>
  )
}

export default CaseListItem
